import "swiper/css";
import "./styles.scss";

import { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import classnames from "classnames";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import { useApp } from "context/app";

import Button from "components/Button";
import BilledButtons from "./components/BilledButtons";
import BilledSwitcher from "./components/BilledSwitcher";
import Feature from "./components/Feature";

import plans_data from "libs/plans_data";
import priceRender from "libs/helpers/priceRender";
import useResponsive from "libs/helpers/useResponsive";

import { ReactComponent as ExternalLinkIcon } from "assets/icons/new/external-link.svg";
import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";
import { ReactComponent as CheckmarkIcon } from "assets/icons/new/check.svg";

const mainClass = "new-website-content__choose-plan__subscription-plan";

const SubscriptionPlan = ({ current }) => {
  const { t } = useTranslation();
  const { max } = useResponsive();

  const { plans_list } = useApp();
  const { values, setFieldValue, errors, touched } = useFormikContext();

  const [explanation, setExplanation] = useState([]);

  const plans = plans_list?.map((item, index) => ({
    ...plans_data?.[index],
    ...item,
  }));

  const BillingControl = max.sm ? BilledButtons : BilledSwitcher;
  const Content = max.sm ? Swiper : "div";
  const Plan = max.sm ? SwiperSlide : "div";
  const selectedPlanIndex = plans?.findIndex(({ prices }) =>
    prices?.some((price) => price.id === values?.plan),
  );
  const initialSlide =
    selectedPlanIndex >= 0
      ? selectedPlanIndex
      : Math.min(1, Math.max(0, (plans?.length ?? 0) - 1));

  return (
    <div className={mainClass}>
      <div className={`${mainClass}__header`}>
        <div className={`${mainClass}__header__item`}>
          <BillingControl
            value={values?.billed_type}
            onChange={(value) => setFieldValue("billed_type", value)}
          />
        </div>
      </div>
      <Content
        className={classnames(`${mainClass}__content`, {
          [`${mainClass}__content--error`]: errors?.plan && !!touched?.plan,
        })}
        {...(max.sm
          ? {
              initialSlide,
              slidesPerView: 1.2,
              centeredSlides: true,
              loop: false,
              spaceBetween: 16,
              breakpoints: {
                600: { slidesPerView: 1.5 },
              },
            }
          : {})}
      >
        {plans?.map(({ icon, name, description, prices, features }) => {
          const current_price = prices?.find(
            (item) => item?.contract_period === values?.billed_type,
          );
          const selected_plan = values?.plan === current_price?.id;
          const current_plan = current?.plan === current_price?.id;

          return (
            <Plan
              key={current_price?.id}
              className={classnames(`${mainClass}__content__item`, {
                [`${mainClass}__content__item--active`]: selected_plan,
                [`${mainClass}__content__item--current`]: current_plan,
              })}
            >
              <div className={`${mainClass}__content__item__header`}>
                <div className={`${mainClass}__content__item__header__icon`}>
                  {icon}
                </div>
                <div className={`${mainClass}__content__item__header__details`}>
                  <div className={`${mainClass}__content__item__type`}>
                    <h3>{name}</h3>
                    <p>{t(description)}</p>
                  </div>
                  <div className={`${mainClass}__content__item__price`}>
                    <h1>
                      {priceRender(
                        current_price?.price?.amount / 100,
                        current_price?.price?.currency,
                      )}
                    </h1>
                    <div className={`${mainClass}__content__item__price__info`}>
                      <span>{t("per Month")}</span>
                      <span>{t("with tax")}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={`${mainClass}__content__item__body`}>
                <div className={`${mainClass}__content__item__features`}>
                  {features?.map(({ label, value, hint }, index) => {
                    const labelParsed = t(label);
                    const valueParsed = !!value
                      ? `${value} ${value.endsWith("h") ? "/" : ""} ${t("mo.")}`
                      : undefined;

                    return (
                      <div
                        key={index}
                        className={`${mainClass}__content__item__features__item`}
                      >
                        <Feature
                          label={labelParsed}
                          value={valueParsed}
                          onClick={() => {
                            setExplanation((prev) => {
                              const filtered = prev.filter(
                                (item) => item.id !== current_price?.id,
                              );

                              return [
                                ...filtered,
                                {
                                  id: current_price?.id,
                                  label: labelParsed,
                                  value: valueParsed,
                                  hint: t(hint),
                                },
                              ];
                            });
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
                {explanation
                  .filter((item) => item.id === current_price?.id)
                  .map((item, idx) => (
                    <div
                      key={idx}
                      className={`${mainClass}__content__item__explanation`}
                    >
                      <Feature label={item.label} value={item.value} />
                      <div
                        className={`${mainClass}__content__item__explanation__hint`}
                      >
                        {item.hint}
                      </div>
                      <button
                        className={`${mainClass}__content__item__explanation__cta`}
                        type="button"
                        onClick={() =>
                          setExplanation((prev) =>
                            prev.filter((ex) => ex.id !== item.id),
                          )
                        }
                      >
                        <i>
                          <ArrowIcon />
                        </i>
                        {t("Return")}
                      </button>
                    </div>
                  ))}
                <div className={`${mainClass}__content__item__more`}>
                  <a
                    href={t("more_details_link")}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("More details")}
                    <i>
                      <ExternalLinkIcon />
                    </i>
                  </a>
                </div>
                <div className={`${mainClass}__content__item__choose`}>
                  <Button
                    variant={
                      !current_plan
                        ? selected_plan
                          ? "active"
                          : "transparent"
                        : undefined
                    }
                    icon={
                      selected_plan && !current_plan ? <CheckmarkIcon /> : null
                    }
                    disabled={current_plan}
                    onClick={
                      !current_plan
                        ? () => setFieldValue("plan", current_price?.id)
                        : undefined
                    }
                  >
                    {t(
                      current_plan
                        ? "Current plan"
                        : selected_plan
                          ? "Selected plan"
                          : "Choose a plan",
                    )}
                  </Button>
                </div>
                <div className={`${mainClass}__content__item__terms`}>
                  <a
                    href={t("more_details_link")}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Terms of Use")}
                    <i>
                      <ExternalLinkIcon />
                    </i>
                  </a>
                </div>
              </div>
            </Plan>
          );
        })}
      </Content>
    </div>
  );
};

export default SubscriptionPlan;
